import React, { useCallback, useMemo } from 'react';
import { Button, Container, Image, Stack } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import parse from 'html-react-parser';
import { sanitizeHtml } from 'components/sanitizeHtml';
import useTranslate from 'hooks/useTranslate';
import SearchJobs from 'components/Common/Jobs/SearchJobs';
import useAuthStore from 'store/auth';
import { USER_TYPES, APP_STORE, PLAY_STORE } from 'constants/index';
import './styles.scss';
import { useLocation } from 'react-router-dom';

const Banner: React.FC = () => {
	const location = useLocation();
	const { translate } = useTranslate();
	const { user, token }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	// Memoize banner class calculation
	const bannerClass = useMemo(() => {
		if (location.pathname === '/interactive-demo') {
			return 'mb-4';
		}
		if (location.pathname === '/static/profile') {
			return 'mb-4';
		}
		return 'banner bg-white';
	}, [location.pathname]);

	// Memoize event handlers
	const handlePlayStoreClick = useCallback(() => {
		window.open(PLAY_STORE, '_blank', 'noopener,noreferrer');
	}, []);

	const handleAppStoreClick = useCallback(() => {
		window.open(APP_STORE, '_blank', 'noopener,noreferrer');
	}, []);

	return (
		<section className={bannerClass}>
			<Container>
				<Stack className="banner-inner align-items-center justify-content-center">
					{token && (
						<div className="banner-inner-heading">
							<h1>{parse(sanitizeHtml(translate('Home.bannerTitle')))}</h1>
						</div>
					)}
					<div className="get-app-buttons d-flex gap-2 gap-md-3">
						<Button
							className="bg-white"
							onClick={handlePlayStoreClick}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Download from Google Play Store"
						>
							<Image
								src={IMAGE_PATHS.GooglePlayIcon}
								className="h-100 w-100 object-fit-contain"
								alt="Google Play Store Icon"
								loading="lazy"
								width={200}
								height={65}
							/>
						</Button>
						<Button
							className="bg-white"
							onClick={handleAppStoreClick}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Download from Apple App Store"
						>
							<Image
								src={IMAGE_PATHS.AppStoreIcon}
								className="h-100 w-100 object-fit-contain"
								alt="Apple App Store Icon"
								loading="lazy"
								width={200}
								height={65}
							/>
						</Button>
					</div>
					{user?.userType === USER_TYPES.SEEKER && token && (
						<div className="banner-filters w-100">
							<SearchJobs handleJobCategoryChange={() => {}} />
						</div>
					)}
				</Stack>
			</Container>
		</section>
	);
};

// Add display name for debugging
Banner.displayName = 'Banner';

export default React.memo(Banner);
